/**
 * @typedef {Object} WorkflowSnapshot
 * @property {string} channel
 * @property {number} revision
 * @property {string} scopeKey
 * @property {Object} payload
 */

export const WORKFLOW_CHANNELS = Object.freeze({
  ADVISER_SCOPE_DIAGNOSTICS: 'mitus:adviser-scope-diagnostics',
  APPLICATION_FLUSH_FLOW: 'fsp-flush-to-model',
  APPLICATION_FLUSH_TRANSITION: 'gen-transition-flush-workspace',
  APPLICATION_FLUSH_WORKSPACE: 'mitus-flush-workspace',
  APPLICATION_MODULE_OPEN: 'mitus-open-module',
  AUTH_STATE: 'mitus-auth-state-changed',
  BUSINESS_PROFILE_SAVED: 'mitus-business-profile-workspace-saved',
  DIARY_RECORDS: 'mitus-diary-records-updated',
  DISCOVERY_PROGRESS: 'mitus-discovery-progress-updated',
  FAMILY_CHARTER_ITEMS: 'mitus-family-charter-module-items-changed',
  FINANCIAL_UPLOAD_REFRESH: 'mitus-financial-upload-refresh-requested',
  FINANCIAL_WORKSPACE: 'mitus-financial-accounts-updated',
  FRONT_OFFICE_SYNC: 'mitus:front-office-sync',
  IDP_INCIDENT_OPEN: 'mitus:idp-open-incident',
  MODELLING_STRUCTURE_REFRESH: 'mitus-modelling-structure-refresh',
  PROJECT_PLAN_TRACKING: 'mitus-project-plan-tracking-updated',
  REPOSITORY_REFRESH: 'mitus-repository-refresh-requested',
  TABLE_BULK_SELECTION: 'tn-cs-bulk',
  TABLE_HEIGHT: 'tn-table-height',
  TABLE_HEIGHT_SYNC: 'tn-sync-table-height',
  TABLE_NODE_REMOVE: 'table-node-remove',
  WORKSPACE_CUSTOMER_DISCOVERY: 'mitus-workspace-customer-discovery-changed',
  WORKSPACE_CUSTOMER_OPTIONS: 'mitus-workspace-customer-options-refreshed',
});

const allowedChannels = new Set(Object.values(WORKFLOW_CHANNELS));
const customerScopedChannels = new Set([
  WORKFLOW_CHANNELS.BUSINESS_PROFILE_SAVED,
  WORKFLOW_CHANNELS.DIARY_RECORDS,
  WORKFLOW_CHANNELS.DISCOVERY_PROGRESS,
  WORKFLOW_CHANNELS.FAMILY_CHARTER_ITEMS,
  WORKFLOW_CHANNELS.FINANCIAL_UPLOAD_REFRESH,
  WORKFLOW_CHANNELS.FINANCIAL_WORKSPACE,
  WORKFLOW_CHANNELS.FRONT_OFFICE_SYNC,
  WORKFLOW_CHANNELS.MODELLING_STRUCTURE_REFRESH,
  WORKFLOW_CHANNELS.PROJECT_PLAN_TRACKING,
  WORKFLOW_CHANNELS.REPOSITORY_REFRESH,
]);
const revisions = new Map();
const listeners = new Map();
const snapshots = new Map();

function validateChannel(channel) {
  if (!allowedChannels.has(channel)) {
    throw new TypeError(`Unknown cross-feature workflow channel: ${String(channel)}`);
  }
  return channel;
}

function normalizePayload(payload) {
  if (payload == null) return Object.freeze({});
  if (typeof payload !== 'object' || Array.isArray(payload)) {
    throw new TypeError('Cross-feature workflow payload must be an object.');
  }
  return Object.freeze({ ...payload });
}

function scopeKey(value = {}) {
  const source = value?.scope && typeof value.scope === 'object' ? value.scope : value;
  const customerId = String(source?.customerId || source?.familyGroupId || '').trim();
  const familyId = String(source?.familyId || source?.familyMemberId || customerId).trim();
  return customerId ? `${customerId}::${familyId || customerId}` : '';
}

function createWorkflowSnapshot(channel, payload) {
  const validChannel = validateChannel(channel);
  const resolvedScopeKey = scopeKey(payload);
  if (customerScopedChannels.has(validChannel) && !resolvedScopeKey) {
    throw new TypeError(`Customer-scoped workflow publication requires customerId: ${validChannel}`);
  }
  const storageKey = `${validChannel}|${resolvedScopeKey}`;
  const revision = (revisions.get(storageKey) || 0) + 1;
  revisions.set(storageKey, revision);
  const next = Object.freeze({
    channel: validChannel,
    revision,
    scopeKey: resolvedScopeKey,
    payload: normalizePayload(payload),
  });
  snapshots.set(storageKey, next);
  return next;
}

/** @returns {WorkflowSnapshot} */
export function publishWorkflowAction(channel, payload = {}) {
  const next = createWorkflowSnapshot(channel, payload);
  for (const listener of listeners.get(next.channel) || []) listener(next);
  return next;
}

/** Await every listener without changing the synchronous publication contract. */
export async function publishWorkflowActionAndWait(channel, payload = {}) {
  const snapshot = createWorkflowSnapshot(channel, payload);
  const pending = [];
  for (const listener of listeners.get(snapshot.channel) || []) {
    try {
      pending.push(Promise.resolve(listener(snapshot)));
    } catch (error) {
      pending.push(Promise.reject(error));
    }
  }
  const settled = await Promise.allSettled(pending);
  const failed = settled.find(result => result.status === 'rejected');
  if (failed) throw failed.reason;
  const results = settled.map(result => result.value);
  return { snapshot, results, ok: results.every(result => result !== false && result?.ok !== false) };
}

export function readWorkflowPayload(channel, options = {}) {
  const validChannel = validateChannel(channel);
  const expectedScopeKey = scopeKey(options);
  if (customerScopedChannels.has(validChannel) && !expectedScopeKey) {
    throw new TypeError(`Customer-scoped workflow read requires customerId: ${validChannel}`);
  }
  return snapshots.get(`${validChannel}|${expectedScopeKey}`)?.payload || Object.freeze({});
}

export function subscribeWorkflowChannel(channel, listener, options = {}) {
  const validChannel = validateChannel(channel);
  if (typeof listener !== 'function') throw new TypeError('Workflow subscriber must be a function.');
  const expectedScopeKey = scopeKey(options);
  if (customerScopedChannels.has(validChannel) && !expectedScopeKey) {
    // A module can mount before the asynchronous customer directory has selected
    // its first customer. An absent scope means there is nothing legitimate to
    // subscribe to yet; the owning React effect will subscribe on the next render.
    return () => {};
  }
  const scopedListener = expectedScopeKey
    ? (snapshot) => { if (snapshot.scopeKey === expectedScopeKey) return listener(snapshot); }
    : listener;
  const channelListeners = listeners.get(validChannel) || new Set();
  channelListeners.add(scopedListener);
  listeners.set(validChannel, channelListeners);
  return () => {
    channelListeners.delete(scopedListener);
    if (!channelListeners.size) listeners.delete(validChannel);
  };
}

export function selectWorkflowPayload(snapshot) {
  return snapshot?.payload && typeof snapshot.payload === 'object' ? snapshot.payload : Object.freeze({});
}
